import {
    EnumButtonName,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import isSSR from '@calm/web-common/dist/isSSR';
import { InvertedButton } from '@rhg/components/inverted-button';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useHistory } from '@rhg/hooks/use-history';
import commonMessages from '@rhg/lang/messages/common';
import { SxProps, Theme, ButtonProps } from '@rhg/libs/material';
import { ChevronLeft } from '@rhg/libs/material/icons';
import { styleArrowBackIosCorrector, sxAppend } from '@rhg/libs/material/sx';
import { useIntl } from 'react-intl';

export const styleBackButtonPositioning = {
    left: (theme: Theme) => ({ xs: 0, xl: theme.semantics.Spacing[32] }),
    top: (theme: Theme) => ({ xs: 0, xl: theme.semantics.Spacing[40] }),
    position: { xs: 'relative', xl: 'absolute' },
};

// This component assumes that the page container is position: relative.
const styleBackButton = {
    color: (theme: Theme) =>
        theme.palette.mode === 'light' // This conditional is backwards because it is applied on an inverted theme
            ? DS_COLOR.DARK_PRIMARY_MAIN
            : DS_COLOR.LIGHT_PRIMARY_MAIN,
    minWidth: 'initial',
    padding: 3,
    zIndex: (theme: Theme) => theme.zIndex.fab,
};

interface BackButtonViewProps {
    fallbackRoute?: string;
    sx?: SxProps<Theme>;
    onClick?: () => void;
    willNavigate?: boolean;
    trackButton: EnumButtonName;
    trackScreen?: EnumScreenName;
    trackProps?: EventProperties;
    ButtonProps?: Partial<ButtonProps>;
}

export function BackButtonView({
    trackButton,
    trackScreen,
    trackProps,
    onClick,
    sx,
    ButtonProps,
}: BackButtonViewProps) {
    const { formatMessage } = useIntl();
    const currentBreakpoint = useCurrentBreakpointName();

    return (
        <InvertedButton
            trackScreen={trackScreen}
            trackButton={trackButton}
            trackProps={trackProps}
            onClick={onClick}
            sx={sxAppend(styleBackButton, sx)}
            tabIndex={0}
            aria-label={formatMessage(
                commonMessages['common.back_button_label']
            )}
            size={
                currentBreakpoint === 'xs' || currentBreakpoint === 'sm'
                    ? 'small'
                    : 'large'
            }
            {...ButtonProps}
        >
            <ChevronLeft sx={styleArrowBackIosCorrector} />
        </InvertedButton>
    );
}

interface BackButtonProps {
    fallbackRoute?: string;
    sx?: SxProps<Theme>;
    onClick?: () => void;
    willNavigate?: boolean;
    trackButton?: EnumButtonName;
    trackProps?: EventProperties;
}

const BackButton = ({
    sx,
    fallbackRoute,
    trackButton = EnumButtonName.BackButton,
    trackProps,
}: BackButtonProps) => {
    const { back } = useHistory();
    const handleBack = () => {
        if (!isSSR()) {
            back(fallbackRoute);
        }
    };

    return (
        <BackButtonView
            trackButton={trackButton}
            trackProps={trackProps}
            onClick={handleBack}
            sx={sxAppend(styleBackButtonPositioning, sx)}
        />
    );
};

export default BackButton;
